import React from "react";
import { Badge } from "react-bootstrap";
import { InfoCircleFill, ExclamationTriangleFill } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { AssignmentNotification } from "../../../../types/scheduling/meetings";
import { Speaker, TalkMod, WMSchedule } from "../../../../types/scheduling/weekend";
import { hasAbsence } from "../../../../helpers/absence";
import { nameOfUser } from "../../../../helpers/user";
import { CongSettings } from "../../../../types/scheduling/settings";
import { NotificationIcon, notificationStatePart } from "../../notificationStatus";
import { OverlappingAssignment } from "../../common";
import SlashCircleFillReversed from "../../../common/SlashCircleFillReversed";
import { AssignmentSummary } from "../../RecentAssignments";
import { haveAVAAssignment, haveWMCoreAssignment } from "../../common";
import { noWeekendMeeting } from "../../eventAlert";
import { TemplateActionButton } from "./TemplateActionButton";
import { Button } from "react-bootstrap";
import { dateToString, getDayjs, weekOf } from "../../../../helpers/dateHelpers";
import HourglassGlobals from "../../../../helpers/globals";

interface IncomingSpeakerInfoProps {
  onSpeakerInfoClick: (speaker: Speaker) => void;
  schedule: WMSchedule;
  speaker2?: Speaker;
  meetingDate: Date;
  absences?: any[];
  notifications?: AssignmentNotification[];
  congSettings: CongSettings;
  userAssignmentMap: Map<number, AssignmentSummary[]>;
  localCongIds: Set<number>;
  editMode: string;
  setEditMode: (mode: string) => void;
  langGroupId: number;
}

export const IncomingSpeakerInfo: React.FC<IncomingSpeakerInfoProps> = (props) => {
  const { t } = useTranslation();

  const speaker = props.speaker2 ? props.speaker2 : props.schedule.speaker;
  const hasConflictingAbsence = speaker?.userId ? hasAbsence(speaker.userId, props.meetingDate, props.absences) : false;
  const notification = props.notifications?.find((n) => n.date === props.schedule.date && n.part === props.schedule.id);
  const notificationState = notificationStatePart(speaker?.userId ?? 0, notification);

  const haveOverlappingAssignment = (): boolean => {
    if (!speaker?.userId || props.localCongIds.has(speaker?.congregation?.id ?? 0)) return false;
    const avaAssignment = haveAVAAssignment(
      speaker?.userId,
      props.schedule.date,
      props.congSettings,
      "weekend",
      props.userAssignmentMap,
    );
    if (avaAssignment) {
      return true;
    }

    if (haveWMCoreAssignment(speaker.userId, props.schedule.date, props.userAssignmentMap)) return true;
    if (props.schedule.out) return props.schedule.out.some((pta) => pta.speaker?.userId === speaker.userId);
    return false;
  };

  const isPastWeek = getDayjs(props.schedule.date).isBefore(weekOf(new Date()), "day");

  return (
    <div className="d-flex flex-row justify-content-between align-items-center flex-wrap">
      <div className="d-flex flex-row align-items-center">
        <h4 className="fw-bold mb-0 me-2">
          {props.speaker2 ? nameOfUser(speaker) : printName(props.schedule, t, props.congSettings)}
        </h4>
        {!!speaker.id && (
          <Button
            size="sm"
            variant="secondary"
            className="border-0 rounded-pill p-2 d-flex align-items-center text-muted"
            onClick={() => {
              props.onSpeakerInfoClick(speaker);
            }}
          >
            <InfoCircleFill size={16} />
          </Button>
        )}
        {props.schedule.confirmed && (
          <Badge bg="success" className="ms-1">
            <span>{t("schedules.weekend.confirmed")}</span>
          </Badge>
        )}
        {(props.localCongIds.has(speaker.congregation?.id ?? 0) || !!notification) && !isPastWeek && (
          <span className="mx-1 d-flex gap-1">
            <NotificationIcon nstate={notificationState} hasAbsence={hasConflictingAbsence} />
            {haveOverlappingAssignment() && <OverlappingAssignment className="" />}
          </span>
        )}
        {hasConflictingAbsence && (
          <SlashCircleFillReversed className="me-1" title={t("schedules.absence.conflicting")} />
        )}
      </div>
      <div className="d-flex flex-row align-items-start">
        {!!speaker.id && (
          <>
            <TemplateActionButton
              settings={props.congSettings}
              schedule={props.schedule}
              meetingDate={dateToString(props.meetingDate)}
              onEditClick={() => {
                props.setEditMode(props.editMode === `week${props.schedule.id}` ? "" : `week${props.schedule.id}`);
              }}
              editMode={props.editMode}
              langGroupId={props.langGroupId}
            />

            {!!props.speaker2 &&
              (props.schedule.talk_mod === TalkMod.CO ||
                props.schedule.event?.event === "co" ||
                noWeekendMeeting(props.schedule.event)) && <ExclamationTriangleFill className="warning-color ms-2" />}
          </>
        )}
      </div>
    </div>
  );
};

// Helper function to print speaker name
function printName(s: WMSchedule, t: (key: string, options?: any) => string, settings?: CongSettings): string {
  if (!s.speaker?.id && !s.talk_mod) {
    return "";
  }

  //if there's a talk assigned, show the speaker
  if (s.speaker?.id) {
    return nameOfUser(s.speaker, HourglassGlobals.nameFmt);
  }

  if (s.talk_mod === TalkMod.TBD && s.speaker.firstname === TalkMod.TBD) {
    return t("schedules.weekend.tbd");
  }

  if (s.talk_mod === TalkMod.Stream && s.speaker.firstname === TalkMod.Stream) {
    return t("schedules.stream");
  }

  if (s.talk_mod === TalkMod.Other) {
    return settings?.other_speaker_name || t("schedules.privileges.other");
  }

  return s.speaker.firstname;
}
